/*
 * @format
 */

import JsonApi from 'devour-client';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';

import logger from '../../components/Logger';
import config from '../../env';

export const PARTNER_ACCESS_REQUESTS_ACCEPT =
  'partner-generated-access-request-accept';

export const PARTNER_ACCESS_REQUEST = 'partner-generated-access-request';

export const ENERGY_USAGE = 'energy-usage';

export default function DiagnosticsJsonApi(Auth) {
  const apiUrl = `${config.diagnostics_api_endpoint}`;
  const jsonApi = new JsonApi({
    apiUrl,
    errorBuilder: error => {
      const { title, detail, status } = error;
      return { title, detail, status };
    },
  });

  const cognitoMiddleware = {
    name: 'get-latest-token',
    req: async payload => {
      const session = await fetchAuthSession();
      const token = session.tokens.idToken.toString();

      /* eslint no-param-reassign: ["error", { "props": false }] */
      payload.req.headers.Authorization = token;
      return payload;
    },
  };

  const requestLogging = {
    name: 'request-logging',
    req: payload => {
      const model = payload?.req?.model || 'Unknown';
      console.log(`${model} API Response `, payload);
      return payload;
    },
  };

  const responseLogging = {
    name: 'response-logging',
    res: payload => {
      const model = payload?.req?.model || 'Unknown';
      if (
        payload &&
        payload.res &&
        payload.res.status >= 200 &&
        payload.res.status < 300
      ) {
        logger.log(`${model} API Response `, payload);
      } else {
        logger.error(`${model} API Response`, payload);
      }

      return payload;
    },
  };

  jsonApi.define(
    'homeowner-session',
    {
      Email: '',
      FirstName: '',
      LastName: '',
      TermsVersion: 0,
      TermsAgreedAt: 0,
      AcceptedMarketing: false,
    },
    {
      collectionPath: 'homeowner-sessions',
    },
  );

  jsonApi.define('homeowner-device-registration-request', {
    SerialNumber: '',
    BoilerType: '',
    BoilerSerialNumber: '',
    LastServiceAt: 0,
    AddressLine1: '',
    AddressCity: '',
    AddressCounty: '',
    AddressPostcode: '',
    MergeRegistration: false,

    RegisteredBy: '',
    RegisteredById: '',
    RegisteredAt: 0,
    WeatherCompensationEnabled: '',

    UpdatedBy: '',
    UpdatedById: '',
    UpdatedAt: 0,
  });

  jsonApi.define('alarm', {
    Location: null,
    Priority: false,
    RaisedAt: 0,
    Severity: '',
    Summary: '',
    Type: '',

    Faults: {
      jsonApi: 'hasMany',
      type: 'faults',
    },
  });

  jsonApi.define('fault', {
    AsfFlags: 0,
    AsfMessage: '',
    IsLockout: false,
    OemFaultCode: 0,
    OemMessage: '',
    ReportedAt: 0,
    ResolutionText: '',
    Severity: 'Critical',
  });

  jsonApi.define('property', {
    AddressCity: '',
    AddressCountry: '',
    AddressCounty: '',
    AddressLine1: '',
    AddressLine2: null,
    AddressPostcode: '',
    Latitude: null,
    Longitude: null,
    Priority: false,

    Partner: {
      jsonApi: 'hasOne',
      type: 'partners',
    },
  });

  jsonApi.define('homeowner-device', {
    SerialNumber: '',
    PrintedSerialNumber: '',
    Type: '',
    RegisteredAt: '',
    RegisteredBy: '',
    RegisteredById: '',
    LastServiceAt: 0,
    ServiceIntervalDays: 0,
    Status: '',
    SystemStatus: '',
    LastSeenAt: '',
    FriendlyName: '',
    BoilerSerialNumber: '',
    BoilerType: '',

    LowWaterPressure: true,
    BoilerMode: '',
    NetworkRssi: '',
    AppVersion: '',

    Alarms: {
      jsonApi: 'hasMany',
      type: 'alarms',
    },

    Property: {
      jsonApi: 'hasOne',
      type: 'properties',
    },

    PartnerAccess: {
      jsonApi: 'hasMany',
      type: 'partner-access',
    },
  });

  jsonApi.define('partner-access', {
    Partner: {
      jsonApi: 'hasOne',
      type: 'partner',
    },

    InviteId: '',
    InstallerEmail: '',
    HomeownerEmail: '',

    AddressLine1: '',
    AddressLine2: '',
    AddressCity: '',
    AddressCounty: '',
    AddressCountry: '',
    AddressPostcode: '',

    CreatedAt: 0,
    CreatedBy: '',
    CreatedByName: '',

    AcceptedBy: '',
    AcceptedAt: 0,
    AcceptedByName: '',
  });

  jsonApi.define('postcode', {
    Postcode: '',
    Town: '',
    Locality: '',
    DoubleDependentLocality: '',
    Thoroughfare: '',
    DependentThoroughfare: '',
    BuildingNumber: '',
    BuildingName: '',
    SubBuildingName: '',
    POBox: '',
    DepartmentName: '',
    OrganisationName: '',
    PoBox: '',
  });

  jsonApi.define('partner', {
    Name: '',
  });

  jsonApi.define('homeowner-generated-access-request', {
    InstallerEmail: '',
    HomeownerEmail: '',
    DeviceRegistrationId: '',
    SupportDuration: '',
    CreatedAt: 0,
    CreatedBy: '',
    CreatedByName: '',
    AddressLine1: '',
    AddressCity: '',
    AddressCounty: '',
    AddressPostcode: '',
    HomeownerDevice: {
      jsonApi: 'hasOne',
      type: 'homeowner-devices',
    },
    Property: {
      jsonApi: 'hasOne',
      type: 'properties',
    },
    Partner: {
      jsonApi: 'hasOne',
      type: 'partners',
    },
  });

  jsonApi.define(PARTNER_ACCESS_REQUEST, {
    CreatedAt: 0,
    CustomerEmail: '',
    CustomerReference: '',
    ExpiresAt: 0,
    PartnerId: '',
    RequestedBy: '',
    RequestedByEmail: '',
    RequestedByName: '',
    Status: '',
    SupportDuration: '',
    Partner: {
      jsonApi: 'hasOne',
      type: 'partners',
    },
  });

  jsonApi.define(PARTNER_ACCESS_REQUESTS_ACCEPT, {
    SupportDuration: '',
    Invite: { jsonApi: 'hasOne', type: 'partner-generated-access-requests' },
    Device: { jsonApi: 'hasOne', type: 'homeowner-devices' },
  });

  jsonApi.define(
    ENERGY_USAGE,
    {
      granularity: '',
      entries: [],
    },
    { collectionPath: 'energy-usage' },
  );

  const cancellableRequest = {
    name: 'axios-cancellable-request',
    req(payload) {
      const { jsonApi: localJsonApi } = payload;
      if (__DEV__) console.log('payload', payload);

      if (!payload.req.params || !payload.req.params.opts) {
        return localJsonApi.axios(payload.req);
      }

      const { opts } = payload.req.params;

      const cancelToken = opts?.cancellationToken;
      const timeout = opts?.timeout;

      if (opts) {
        delete payload.req.params.opts;
      }

      return localJsonApi.axios(
        Object.assign(payload.req, {
          cancelToken,
          timeout,
        }),
      );
    },
  };

  const errorMiddleware = {
    name: 'is-it-a-cancel',
    error(payload) {
      console.log('checking errors');
      if (axios.isCancel(payload)) {
        console.log('Request canceled', payload);

        return {
          cancelled: true,
        };
      } else {
        return payload;
      }
    },
  };

  jsonApi.insertMiddlewareBefore(
    'axios-cancellable-request',
    cognitoMiddleware,
  );

  jsonApi.replaceMiddleware('axios-request', cancellableRequest);
  jsonApi.insertMiddlewareBefore(
    'axios-cancellable-request',
    cognitoMiddleware,
  );
  jsonApi.insertMiddlewareBefore('axios-cancellable-request', requestLogging);

  jsonApi.insertMiddlewareBefore('axios-request', responseLogging);

  jsonApi.insertMiddlewareAfter('errors', errorMiddleware);

  return jsonApi;
}
